import Loading from '@/components_v2/Loading';
import config from '@/Constants';
import { CollectionItem } from '@/modules/core/domain/interfaces/ICollection';
import { QueryResponse } from '@/modules/queryfilter/domain/interfaces';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import {
  Button,
  HStack,
  List,
  ListItem,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useQuery } from 'react-query';

import DebouncedInput from '../../DebouncedInput';
import { ItemsListProps } from './ItemsList.types';

const ItemsList: React.FC<ItemsListProps> = ({
  onSelect,
  selected,
  collectionId,
  questionId,
}) => {
  const [page, setPage] = React.useState(1);
  const [hasNextPage, setHasNextPage] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined,
  );

  React.useEffect(() => {
    setPage(1);
  }, [collectionId]);

  const { data, isLoading } = useQuery<QueryResponse<CollectionItem>>(
    `${config.COLLECTION_ITEMS_URL}?size=20&page=${page}${
      collectionId ? `&collection_id=${collectionId}` : ''
    }${questionId ? `&question_id=${questionId}` : ''}${
      searchQuery ? `&filter[collection_items][search]=${searchQuery}` : ''
    }`,
  );

  const [items, setItems] = React.useState<CollectionItem[]>([]);

  React.useEffect(() => {
    if (data) {
      setItems(data.items);
      setHasNextPage(
        data.hasNextPage ||
          ((data as unknown) as { hasnextpage: boolean }).hasnextpage, //??
      );
    }
  }, [data]);

  const onSearch = React.useCallback((value: string) => {
    if (value.length > 0) {
      setSearchQuery(value);
    } else {
      setSearchQuery(undefined);
    }
    setPage(1);
  }, []);

  return (
    <VStack w="49%">
      <Text>Itens</Text>
      <DebouncedInput onChange={onSearch} placeholder='Pesquisar item' />
      <List h="50vh" overflowY="auto" spacing={1} w="full">
        {isLoading ? (
          <Loading />
        ) : (
          items.map((item) => (
            <ListItem
              p={2}
              bg={selected && selected.id === item.id ? 'blue.100' : 'none'}
              borderRadius="md"
              _hover={{ bg: 'gray.100' }}
              fontWeight="semibold"
              key={item.id}
              onClick={() => onSelect(item)}
              cursor="pointer"
            >
              {item.name}
            </ListItem>
          ))
        )}
      </List>
      <Spacer />
      <HStack
        w="full"
        borderTopWidth="1px"
        borderColor="gray.200"
        py={3}
        justify="center"
        spacing={2}
      >
        {/* Previous Page Button */}
        <Button
          onClick={() => setPage((prev) => prev - 1)}
          size="sm"
          disabled={page <= 1}
          aria-label="Go to previous page"
          _disabled={{
            bg: 'gray.50',
            cursor: 'not-allowed',
            color: 'gray.400',
          }}
          _hover={{ bg: 'gray.100' }}
        >
          <ArrowBackIcon />
        </Button>

        {/* Current Page */}
        <Text
          fontWeight="bold"
          fontSize="md"
          aria-label={`Current page, ${page}`}
        >
          {page}
        </Text>

        {/* Next Page Button */}
        <Button
          onClick={() => setPage((prev) => prev + 1)}
          size="sm"
          disabled={!hasNextPage}
          aria-label="Go to next page"
          _disabled={{
            bg: 'gray.50',
            cursor: 'not-allowed',
            color: 'gray.400',
          }}
          _hover={{ bg: 'gray.100' }}
        >
          <ArrowForwardIcon />
        </Button>
      </HStack>
    </VStack>
  );
};

export default ItemsList;
